// Wall-time helpers of the staff itinerary screens (CHT-747). Two rules hold here:
//   1. The SERVER converts a wall time to a UTC instant (contract section 3.4). The page only ever sends `{ date, time, timeZone,
//      disambiguation? }`. Nothing here produces an instant that a command carries.
//   2. The device zone is never used. Every function takes an explicit IANA zone. The staff detail has stored instants and zones
//      but no `localStart` text (the customer view has), so the page formats a stored instant in the item's OWN zone, for display.
//      The probes that tell a gap or a repeated clock time are hints that place a message; the server decides.

const ZONE_TEXT = /^(UTC|[A-Za-z_]+(\/[A-Za-z0-9_+-]+){1,2})$/u;
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  const known = formatters.get(zone);
  if (known !== undefined) return known;
  const made = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  formatters.set(zone, made);
  return made;
}

/** `true` for text the API accepts as a zone and the runtime knows. */
export function isKnownZone(zone: string): boolean {
  if (!ZONE_TEXT.test(zone)) return false;
  try {
    formatterFor(zone);
    return true;
  } catch {
    return false;
  }
}

export type LocalParts = Readonly<{ date: string; time: string }>;

type Fields = Readonly<{ y: number; mo: number; d: number; h: number; mi: number; s: number }>;

function fieldsAt(ms: number, zone: string): Fields {
  const out: Record<string, number> = {};
  for (const part of formatterFor(zone).formatToParts(new Date(ms))) {
    if (part.type !== "literal") out[part.type] = Number(part.value);
  }
  return {
    y: out.year ?? 0,
    mo: out.month ?? 0,
    d: out.day ?? 0,
    h: out.hour ?? 0,
    mi: out.minute ?? 0,
    s: out.second ?? 0,
  };
}

const two = (value: number) => String(value).padStart(2, "0");

/** The wall date and time an instant shows in `zone`. */
export function localPartsAt(ms: number, zone: string): LocalParts {
  const f = fieldsAt(ms, zone);
  return {
    date: `${String(f.y).padStart(4, "0")}-${two(f.mo)}-${two(f.d)}`,
    time: `${two(f.h)}:${two(f.mi)}`,
  };
}

/** The UTC offset of `zone` at an instant, in minutes (Asia/Kolkata is 330). */
export function offsetMinutesAt(ms: number, zone: string): number {
  const f = fieldsAt(ms, zone);
  const wallAsUtc = Date.UTC(f.y, f.mo - 1, f.d, f.h, f.mi, f.s);
  return Math.round((wallAsUtc - Math.floor(ms / 1000) * 1000) / MINUTE);
}

export function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  return `UTC${sign}${two(Math.floor(abs / 60))}:${two(abs % 60)}`;
}

export type WallTimeKind = "UNIQUE" | "GAP" | "OVERLAP" | "INVALID";

/**
 * Whether a wall time exists in a zone: one instant (`UNIQUE`), none (`GAP`: the clocks jump over it) or two (`OVERLAP`: the clocks
 * go back). It probes the zone offset around the naive wall time and keeps the offsets whose local parts read back exactly, the
 * method of the server. `instants` are sorted, earliest first.
 */
export function wallTimeKind(
  date: string,
  time: string,
  zone: string,
): Readonly<{ kind: WallTimeKind; instants: readonly number[] }> {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(date);
  const clock = /^([01]\d|2[0-3]):([0-5]\d)$/u.exec(time);
  if (match === null || clock === null || !isKnownZone(zone))
    return { kind: "INVALID", instants: [] };
  const naive = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(clock[1]),
    Number(clock[2]),
  );
  const offsets = new Set<number>();
  for (const shift of [-36 * HOUR, -12 * HOUR, 0, 12 * HOUR, 36 * HOUR]) {
    offsets.add(offsetMinutesAt(naive + shift, zone));
  }
  const instants = new Set<number>();
  for (const offset of offsets) {
    const candidate = naive - offset * MINUTE;
    const read = localPartsAt(candidate, zone);
    if (read.date === date && read.time === time) instants.add(candidate);
  }
  const sorted = [...instants].sort((a, b) => a - b);
  const kind = sorted.length === 0 ? "GAP" : sorted.length === 1 ? "UNIQUE" : "OVERLAP";
  return { kind, instants: sorted };
}

/** `true` when the wall time an instant shows in `zone` happens twice that day (a daylight saving overlap). */
export function isRepeatedWallTime(ms: number, zone: string): boolean {
  const { date, time } = localPartsAt(ms, zone);
  return wallTimeKind(date, time, zone).kind === "OVERLAP";
}

/** Which of the two repeated times an instant is: `earlier` or `later`; `null` when it is not repeated. */
export function repeatedChoiceOf(ms: number, zone: string): "earlier" | "later" | null {
  const { date, time } = localPartsAt(ms, zone);
  const { kind, instants } = wallTimeKind(date, time, zone);
  if (kind !== "OVERLAP") return null;
  return instants[0] === Math.floor(ms / MINUTE) * MINUTE ? "earlier" : "later";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** `2026-10-25` as `Sun 25 Oct 2026`. Pure calendar arithmetic: no zone is involved. */
export function formatDay(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(date);
  if (match === null) return date;
  const at = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return `${WEEKDAYS[at.getUTCDay()]} ${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()}`;
}

/** `75` as `1 h 15 min`. */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** Whole minutes between two instants (ISO text). */
export function minutesBetween(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / MINUTE);
}

/** The zone names the runtime knows, for the picker. Empty when the runtime cannot list them (the field still takes typed text). */
export function knownZoneNames(): readonly string[] {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [];
  }
}
